import {
  Component,
  inject,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@softline/core';
import { Router } from '@angular/router';
import {
  BackNavigationService,
  BlockingLoadingSpinnerComponent,
  showRequestErrors,
  HeaderComponent,
  ScanButtonComponent,
  ScannerStore,
  SOFTLINE_FEATURE_SCANNER,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG } from '../../lagerplatz-umlagerung.shared';
import { LagerplatzUmlagerungStore } from '../../store';
import {
  ArtikelMenge,
  ArtikelMengeAuswahlComponent,
  MischPalette,
  MixedPalletComponent,
  openSelectUnitDialog,
} from '@softapps/lager/core';
import {
  asapScheduler,
  BehaviorSubject,
  combineLatestWith,
  debounceTime,
  map,
  observeOn,
  skip,
  startWith,
  Subject,
  Subscription,
} from 'rxjs';
import { SearchInputComponent, TranslatePipe } from '@softline/ui-core';
import { type ProgressStep, ProgressStepsComponent } from '@softline/ui-core/progress';
import {
  PageComponent,
  PageHeaderComponent,
  Panel,
  PanelHeaderComponent,
} from '@softapps/core/pages';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'soft-artikel-auswahl',
  imports: [
    CommonModule,
    ProgressStepsComponent,
    ArtikelMengeAuswahlComponent,
    ScanButtonComponent,
    BlockingLoadingSpinnerComponent,
    HeaderComponent,
    MixedPalletComponent,
    TranslatePipe,
    PageComponent,
    PageHeaderComponent,
    Panel,
    PanelHeaderComponent,
    SearchInputComponent,
  ],
  templateUrl: './artikel-auswahl.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./artikel-auswahl.page.css'],
})
export class ArtikelAuswahlPage implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly backNavigationService = inject(BackNavigationService);

  private readonly openSelectUnitDialog = openSelectUnitDialog();
  protected readonly searchValue$ = new Subject<string | null>();

  readonly steps: ProgressStep[] = [
    {
      header: '#LAGERPLATZ_UMLAGERUNG.PAGES.QUELLLAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERPLATZ_UMLAGERUNG.PAGES.QUELLLAGERPLATZ_SCANNEN.DESCRIPTION',
    },
    {
      header: '#LAGERPLATZ_UMLAGERUNG.PAGES.ARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERPLATZ_UMLAGERUNG.PAGES.ARTIKEL_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERPLATZ_UMLAGERUNG.PAGES.MENGEN_AUSWAHL.TITLE',
      subHeader: '#LAGERPLATZ_UMLAGERUNG.PAGES.MENGEN_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERPLATZ_UMLAGERUNG.PAGES.ZIELLAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERPLATZ_UMLAGERUNG.PAGES.ZIELLAGERPLATZ_SCANNEN.DESCRIPTION',
    },
  ];

  private subscription?: Subscription;

  loading$ = new BehaviorSubject<boolean>(false);

  quellLagerplatz$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
    LagerplatzUmlagerungStore.getters.quellLagerplatz
  );

  protected readonly filteredStorageLocationData = toSignal(
    this.searchValue$.pipe(
      startWith(null),
      debounceTime(350),
      combineLatestWith(this.quellLagerplatz$),
      map(([searchValue, storageLocation]) => {
        const inhalt = storageLocation?.inhalt ?? [];
        const search = searchValue?.trim().toLowerCase();
        if (!search) return inhalt;

        return inhalt.filter((o) =>
          Object.values(o.artikel ?? {}).some(
            (value) =>
              value !== null &&
              value !== undefined &&
              String(value).toLowerCase().includes(search)
          )
        );
      })
    )
  );

  constructor() {}

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#LAGERPLATZ_UMLAGERUNG.TITLE'
    );
    this.subscription = this.store
      .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
      .pipe(skip(1), observeOn(asapScheduler))
      .subscribe(async (scan) => {
        if (!scan || this.loading$.value) return;

        this.loading$.next(true);
        try {
          await this.store.dispatch(
            SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
            LagerplatzUmlagerungStore.actions.loadPalette,
            scan.data
          );
          this.loading$.next(false);
          await this.router.navigate(['/lagerplatz-umlagerung', 'mengen-auswahl']);
        } catch (e) {
          this.loading$.next(false);
          showRequestErrors(this.store, e);
        }
      });
  }

  ngOnDestroy() {
    if (this.subscription && !this.subscription.closed) this.subscription.unsubscribe();
    this.subscription = undefined;
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/lagerplatz-umlagerung']);
  }

  async onSelect(artikel: ArtikelMenge): Promise<void> {
    let selectedArtikel = { ...artikel };

    if (
      artikel.artikel.kzartikelart === 'DIF' ||
      artikel.artikel.kzartikelart === 'SDIF'
    ) {
      const result = await this.openSelectUnitDialog(artikel.einheitenAuswahl ?? []);
      if (!result || result === 'DISMISSED') return;
      selectedArtikel = {
        ...selectedArtikel,
        einheit: result.einheit,
        menge: result.menge,
      };
    }

    this.store.commit(
      SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
      LagerplatzUmlagerungStore.mutations.setArtikel,
      selectedArtikel
    );
    await this.router.navigate(['/lagerplatz-umlagerung', 'mengen-auswahl']);
  }

  async onSelectMischpalette(mischpalette: MischPalette): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
      LagerplatzUmlagerungStore.mutations.setMischpalette,
      { ...mischpalette }
    );
    await this.router.navigate(['/lagerplatz-umlagerung', 'ziellagerplatz']);
  }
}
